// frontend/src/utils/playerDisplay.ts
import { parseISO, format as formatDateFns, isValid } from 'date-fns';

/**
 * Formats an ISO datetime string into a human-readable time.
 * @param isoString The ISO datetime string (e.g., "2024-08-17T14:00:00Z")
 * @param includeTimezone Whether to include the short timezone offset (e.g., "EDT")
 * @returns Formatted time string (e.g., "2:00 PM") or 'TBD'/'Error'
 */
export const formatMatchTime = (isoString: string | null | undefined, includeTimezone: boolean = false): string => {
    if (!isoString) return 'TBD';
    try {
        const date = parseISO(isoString);
        if (!isValid(date)) {
            return 'Invalid Time';
        }
        // Format to local time. e.g., "2:00 PM" (includeTimezone is optional)
        const formatString = includeTimezone ? "h:mm a zzz" : "h:mm a";
        return formatDateFns(date, formatString);
    } catch (e) {
        console.error('Error formatting match time:', isoString, e);
        return 'Time Error';
    }
};

/**
 * Returns a relative countdown string for a match that hasn't started yet.
 * Returns null if the match has already started (caller should fall back to formatMatchTime).
 * @param isoString The ISO datetime string of the match kickoff
 * @param now The current Date (passed in so the caller controls update frequency)
 * @returns e.g. "47m", "2h 15m", "<1m", or null if started
 */
export const formatMatchCountdown = (
  isoString: string | null | undefined,
  now: Date,
): string | null => {
  if (!isoString) return null;
  try {
    const matchDate = parseISO(isoString);
    if (!isValid(matchDate)) return null;

    const diffMs = matchDate.getTime() - now.getTime();
    if (diffMs <= 0) return null; // match started — caller falls back to formatMatchTime

    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return '<1m';
    if (diffMins < 60) return `${diffMins}m`;

    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins === 0 ? `${hours}h` : `${hours}h ${mins}m`;
  } catch (e) {
    console.error('Error computing match countdown:', isoString, e);
    return null;
  }
};

/**
 * Formats an ISO datetime into a compact kickoff label for upcoming matches.
 * Returns null if the match has already started.
 *
 * Weekday + time ("Sat 11:00 AM") - the same shape as the double-game choice
 * chips, which the owner preferred over "8/30 9:00AM" (2026-08-25). A match
 * more than six days out (next-week previews) keeps a short date
 * ("Sat 9/6 3:00 PM") so a weekday alone can never be ambiguous.
 * @param isoString The ISO datetime string of the match kickoff
 * @param now The current Date
 * @returns e.g. "Sat 11:00 AM", "Sat 9/6 3:00 PM", or null if started
 */
export const formatMatchDateTime = (
  isoString: string | null | undefined,
  now: Date,
): string | null => {
  if (!isoString) return null;
  try {
    const matchDate = parseISO(isoString);
    if (!isValid(matchDate)) return null;
    const msUntil = matchDate.getTime() - now.getTime();
    if (msUntil <= 0) return null;
    const withinWeek = msUntil < 6 * 24 * 60 * 60 * 1000;
    return formatDateFns(matchDate, withinWeek ? 'EEE h:mm a' : 'EEE M/d h:mm a');
  } catch (e) {
    console.error('Error formatting match datetime:', isoString, e);
    return null;
  }
};

/**
 * Returns Tailwind CSS text color class based on OPRK value and league size.
 * Lower OPRK is generally considered a tougher matchup for offensive players.
 * @param oprk Overall opponent rank within its competition
 * @param leagueSize The total number of teams in the league for OPRK context. Defaults to 20 if not provided or invalid.
 * @returns Tailwind text color class string
 */
export const getOprkColorClass = (oprk: number | null | undefined, leagueSize: number = 20): string => {
    if (oprk === null || oprk === undefined || isNaN(oprk)) return 'text-gray-400'; // Neutral for missing data

    // Ensure leagueSize is a positive number, otherwise default to 20.
    const effectiveLeagueSize = (typeof leagueSize === 'number' && leagueSize > 0) ? leagueSize : 20;

    // Calculate 1/3 thresholds dynamically
    // Math.ceil for the first third to ensure it includes enough teams (e.g., 20/3 = 6.66 -> 7)
    // Math.floor for the start of the bottom third (e.g., 2*20/3 = 13.33 -> 13, so bottom third starts at rank 14)
    const topThirdThreshold = Math.ceil(effectiveLeagueSize / 3);
    const bottomThirdStartThreshold = Math.floor((2 * effectiveLeagueSize) / 3) + 1;

    // console.log(`[getOprkColorClass] OPRK: ${oprk}, LeagueSize: ${effectiveLeagueSize}, TopThird: <=${topThirdThreshold}, BottomThird: >=${bottomThirdStartThreshold}`);

    // Lower OPRK values (closer to 1) are "tougher" opponents.
    if (oprk <= topThirdThreshold) return 'text-secondary'; // Tough matchup (bottom ~33% of opponents based on rank)
    if (oprk >= bottomThirdStartThreshold) return 'text-green-400';    // Easier matchup (top ~33% of opponents based on rank)
    return 'text-yellow-400'; // Mid-tier matchup
};

// One explanation for league rows, DFS rows and the scoring-match picker.
export const OPRK_TOOLTIP = 'Overall opponent rank based on fantasy points allowed, weighted with last season. Lower = tougher overall.';
